import { computed, inject, nextTick, provide, ref, useAttrs, watch, watchEffect, type TemplateRef } from "vue";
import { FormKey, type FormProvide } from "vuetify/lib/composables/form.mjs";
import { countCodePoints } from "@common-ui/utilities/characterCount";
import { VuetifyValidation } from "@/helpers/Validation";
import { defaultOverflowMessage, stringifyForCounting } from "@/helpers/characterLimitMessage";

export { defaultOverflowMessage } from "@/helpers/characterLimitMessage";

/** A sync Vuetify field rule FUNCTION: `true` when valid, otherwise the message to display — `ValidationRule.isValid`'s shape. */
type SyncFieldRuleFunction = (value: unknown) => boolean | string;

/** An async Vuetify field rule FUNCTION, for callers that need a server-side validation check. */
type AsyncFieldRuleFunction = (value: unknown) => Promise<boolean | string>;

/** A caller-supplied Vuetify field rule FUNCTION. */
export type FieldRuleFunction = SyncFieldRuleFunction | AsyncFieldRuleFunction;

/**
 * A single entry `CharacterLimitProps.rules` accepts. Vuetify's own `rules` prop type
 * (`node_modules/vuetify/lib/composables/validation.d.ts`'s `ValidationRule`) is not only a function —
 * a caller may also pass a LITERAL `string` (an always-invalid message) or `boolean` directly, and
 * Vuetify's own `validate()` loop (`node_modules/vuetify/lib/composables/validation.js`) applies a
 * literal via `const handler = typeof rule === 'function' ? rule : () => rule;`. `mergedRules` below
 * only wraps FUNCTION entries in `guardCallerRuleAgainstThrows` — a literal entry passes straight
 * through untouched for Vuetify to interpret itself — so this type has to describe both shapes;
 * typing `rules` as `FieldRuleFunction[]` alone would reject a legitimate, documented Vuetify call
 * pattern (LSP-16848 break-it round 4).
 */
export type FieldRule = FieldRuleFunction | boolean | string;

export interface CharacterLimitProps {
	/**
	 * Maximum number of characters, counted as Unicode CODE POINTS (an emoji like 😀 is 1; a ZWJ
	 * sequence like 👨‍👩‍👧‍👦 is as many code points as it's built from — see the doc comment on
	 * `CHARACTER_LIMIT_MODES` below). Code points is the app-wide default counting unit
	 * (LSP-17265, `@common-ui/utilities/characterCount`) — it's also the unit Postgres `varchar(n)`
	 * enforces, so this keeps the client counter at least as strict as the column. Bind an existing
	 * `FIELD_LENGTHS` constant where one fits. Expected to be at least 1. A static template attribute
	 * written without a binding colon (`max-length="3"`) arrives as the STRING `"3"`, so this is typed
	 * `number | string` — declaring it `number` alone made Vue's own runtime prop-type checker warn on
	 * that exact documented, supported shape (LSP-16848 review finding F3). Either shape is coerced
	 * internally (see `coerceMaxLength` below) before it is used numerically, so a static `"3"` behaves
	 * identically to a bound `:max-length="3"` (LSP-16848 break-it round 4).
	 */
	maxLength: number | string;
	/**
	 * Extra rules such as Required. They run BEFORE the limit rule, so on an empty field their
	 * message is the one displayed (Vuetify's `maxErrors` defaults to 1). Each entry is a rule
	 * FUNCTION (`(value) => true | string`) or, per Vuetify's own `rules` contract, a literal
	 * `string`/`boolean` applied as a constant result — see `FieldRule` above.
	 */
	rules?: FieldRule[];
	/**
	 * Overrides the default `Must not exceed N characters.` overflow copy. A caller should pass a
	 * string, but the prop is intentionally typed `unknown` so malformed runtime values can still be
	 * defended against and tested: a `number` or an object with a real `toString` is coerced to text;
	 * anything that cannot be meaningfully turned into text falls back to the default copy instead —
	 * see `resolveOverflowMessage` in useCharacterLimit.ts (LSP-16848 break-it round 5).
	 */
	overLimitMessage?: unknown;
	/**
	 * `true` (default): trim before counting and validating, so the displayed count agrees with the
	 * trimming validator on whitespace-padded input (LSP-15742).
	 * `false`: count raw code points, for a limit that must match an untrimmed server-side count.
	 */
	trim?: boolean;
	/**
	 * Lowercase noun naming this field (e.g. "name", "internal name", "title"), used to build the
	 * default overflow message as "Please enter a shorter {fieldNoun}." instead of the generic
	 * "Must not exceed N characters." Optional and backward-compatible: omitting it keeps the old
	 * generic copy, so existing call sites are unaffected until they opt in. Ignored entirely when
	 * `overLimitMessage` is also supplied — that prop always wins.
	 */
	fieldNoun?: string;
	/**
	 * Opt-in HARD cap: renders a native `maxlength` so the browser refuses keystrokes past
	 * [maxLength], instead of this component's default soft overflow (type past the limit, counter and
	 * field turn red, save blocked by the rule).
	 *
	 * Default `false`, and deliberately so. The repo-wide default is soft overflow — root CLAUDE.md,
	 * "Form field length limits (maxLength) — warn, never hard-cap" (LSP-16465): silently swallowing
	 * keystrokes reads as a broken field, where a red counter explains itself. Do not flip this on a
	 * field without a decision to point at; today the checklist name/title fields are the only opt-ins
	 * (LSP-20477 follow-up, author-directed).
	 *
	 * Note this is the ONLY supported way to get a native maxlength through: a caller's own
	 * `maxlength` attribute is still stripped by `sanitizeWrapperOwnedAttrs`, so the cap can never
	 * arrive by accident — it has to be asked for by name.
	 *
	 * COUNTING UNIT CAVEAT: native `maxlength` counts UTF-16 CODE UNITS while [maxLength] and the
	 * visible counter count CODE POINTS. They agree for every BMP character (ASCII, Latin, CJK …);
	 * they diverge only on astral-plane input, where an emoji is 2+ units but 1+ code points, so the
	 * browser stops slightly EARLIER than the counter's number. That direction is fail-safe (never
	 * over the limit, and comfortably under contact-api's UTF-16 server checks) and is why this is a
	 * plain attribute rather than a truncating watcher on the model — truncating what a user already
	 * typed is the worse half of what LSP-16465 ruled out.
	 */
	hardCap?: boolean;
	/**
	 * Optional transform applied to the value BEFORE both the counter and the limit rule measure it, for
	 * a field whose stored text is not the text that has to fit the limit. LSP-20508: a settings message
	 * template is stored with placeholder tokens (`${user}`, `${organization}`) and sent with those tokens
	 * replaced, so the string the 500-character limit applies to is the EXPANDED one — a caller passes
	 * `countableTemplateText` (helpers/templatePlaceholderCount.ts) here and the counter then shows the
	 * number that actually decides whether the save is refused, instead of a smaller number that reads as
	 * a contradiction next to the refusal.
	 *
	 * Do not combine with [hardCap]: a native `maxlength` caps what the browser lets a user TYPE, which is
	 * the raw value, so a transform that measures anything else would stop the field short of its own
	 * counter with no way to fix it. No call site does both today (the checklist fields are the only
	 * `hardCap` opt-ins and none of them transform).
	 *
	 * Applied in exactly one place (`toCountedValue` below) so the counter/rule pairing
	 * `CHARACTER_LIMIT_MODES` guarantees survives: a transform that moved only the counter would
	 * reintroduce the counter-disagrees-with-rule bug class this component family exists to remove.
	 *
	 * MUST BE STABLE: pass a `computed` that closes over the values it needs (see the three settings
	 * pages), never an inline `:count-as="(text) => expand(text, orgName)"`. This is a source of the
	 * revalidation watch at the bottom of this file — a field the caller already asked to validate has to
	 * re-judge when the transform changes, or an organization rename leaves a stale verdict next to a
	 * counter that has already moved (codex review) — so an inline arrow, which hands over a new identity
	 * on every parent render, would re-`validate()` on each of them and re-invoke every caller rule,
	 * including a slow async one.
	 */
	countAs?: (text: string) => string;
}

/**
 * Trimmed code-point count, mirroring `countCharactersTrimmed`'s trim-then-count shape (LSP-16852/
 * 16853/16854/16855/16859) but counting with `countCodePoints` — the app-wide default unit
 * (LSP-17265) — instead of graphemes. Returns `Infinity` — never `0`, never a thrown error — for a
 * value `stringifyForCounting` could not read as text, so both of `isWithinCharacterLimit`'s
 * branches fail CLOSED for it: the invalid-maxLength branch's `=== 0` check reads false (not valid),
 * and a normal `count <= param` check also reads false (over limit) for any usable `param`.
 */
const countCodePointsTrimmed = (value: unknown) => {
	const stringified = stringifyForCounting(value);
	return stringified === undefined ? Number.POSITIVE_INFINITY : countCodePoints(stringified.trim());
};

/**
 * Untrimmed code-point count. Coerces first so a numeric `0` counts as one character, like the rule
 * does. Fails closed the same way as `countCodePointsTrimmed` above for a value that cannot be read as
 * text (LSP-16852/16853/16854/16855/16859 break-it round 1).
 */
const countCodePointsUntrimmed = (value: unknown) => {
	const stringified = stringifyForCounting(value);
	return stringified === undefined ? Number.POSITIVE_INFINITY : countCodePoints(stringified);
};

/**
 * The two counting modes. Each pairs a counter function with the rule factory that measures the SAME
 * way, declared side by side so the pair cannot drift — a counter that disagrees with its rule is the
 * whole bug class this component family exists to remove (LSP-16847), so `trim` is branched on in
 * exactly one place. Both halves of both entries count Unicode CODE POINTS (LSP-17265's app-wide
 * default unit, `@common-ui/utilities/characterCount`'s `countCodePoints`) — not graphemes — via the
 * `MaxCodePointsValidation`/`MaxCodePointsValidationUntrimmed` factories in `Validation.ts`, which are
 * additive siblings of `MaxLengthValidation`/`MaxLengthValidationUntrimmed` kept solely for this pair
 * so the 21 other grapheme-based consumers of those two are untouched.
 */
const CHARACTER_LIMIT_MODES = {
	trimmed: { countCharactersFor: countCodePointsTrimmed, createLimitRule: VuetifyValidation.MaxCodePointsValidation },
	untrimmed: { countCharactersFor: countCodePointsUntrimmed, createLimitRule: VuetifyValidation.MaxCodePointsValidationUntrimmed }
} as const;

/**
 * Pure predicate version of `limitRule`'s counting/threshold check below — same `CHARACTER_LIMIT_MODES`
 * counters, same fail-closed handling of an unusable `maxLength` via `isInvalidMaxLength` (only an EMPTY
 * value is valid for an invalid limit; Required is the caller's own rule, not ours). For callers that
 * need a plain boolean OUTSIDE a Vuetify `rules` array — typically a field that can be unmounted (a
 * collapsed section drops its rules from the enclosing `v-form` with it, e.g. SheetEditor.vue's notes
 * field) or was never wrapped in a `v-form` at all (FormPage.vue's button text). `limitRule` below and
 * both of those external callers share this single implementation so "is this string within the limit"
 * can never drift between the three call sites (LSP-16852/16853/16854/16855/16859).
 */
export function isWithinCharacterLimit(value: unknown, maxLength: number, trim = true): boolean {
	const mode = trim ? CHARACTER_LIMIT_MODES.trimmed : CHARACTER_LIMIT_MODES.untrimmed;
	if (isInvalidMaxLength(maxLength)) {
		return mode.countCharactersFor(value) === 0;
	}
	return mode.createLimitRule(coerceMaxLength(maxLength), "").isValid(value) === true;
}

/**
 * Wraps a single caller-supplied rule FUNCTION so a thrown error — sync throw or rejected `Promise` — is
 * converted into a passing result instead of propagating out of Vuetify's own `validate()` loop
 * (`node_modules/vuetify/lib/composables/validation.js`: `const result = await handler(validationModel.
 * value);`, with no `try`/`catch` around it). Unguarded, that throw rejects the WHOLE `validate()`
 * call — the loop never reaches the rules after the broken one, so `mergedRules`' own limit rule below
 * never runs and an over-limit value is never reported; the exposed `validate()` rejects instead of
 * resolving with messages (LSP-16848 break-it round 3).
 *
 * Returns `true` for the broken rule itself — Vuetify's own "this rule passed" signal — rather than an
 * invalid string, precisely so the per-`validate()` `maxErrors` budget (default `1`) is not spent on a
 * rule we cannot trust to report anything meaningful; that leaves the budget free for the limit rule
 * below to report the real overflow instead of a caller-rule message we invented. The actual "something
 * is wrong" signal is the `console.warn` — a diagnostic naming the component, per root CLAUDE.md's
 * "Diagnostic logging when fixing silent failures" — so a caller with a broken custom rule sees it in
 * their console rather than silently having their rule's verdict replaced with "always passes".
 *
 * Always returns a `Promise` — even for a rule that itself resolves synchronously — both because that
 * is the only way to `await` (and so `try`/`catch`) a rule that MIGHT throw asynchronously instead of
 * synchronously, and because it gives the wrapper a single, uniform return type (see
 * `AsyncFieldRuleFunction` above) rather than a type that changes shape per call. This adds no
 * observable delay: Vuetify's own loop already does `await handler(value)` on every rule regardless, and
 * `await` on an already-resolved value takes the same one microtask turn as `await`ing a `Promise.
 * resolve` of it.
 *
 * Invokes the underlying rule EXACTLY ONCE regardless of whether it throws synchronously or returns a
 * rejecting `Promise` — caller rules can have side effects (e.g. a server round-trip), so this must
 * never retry on failure.
 *
 * Only ever called with an actual FUNCTION rule — `mergedRules` below gates the call on `typeof rule
 * === "function"` and passes a LITERAL `string`/`boolean` rule through unchanged instead. *Calling* a
 * literal as if it were a function throws on the literal itself (`"a string" is not a function`), which
 * this guard would then catch and report as passing — silently discarding the caller's literal rule
 * (LSP-16848 break-it round 4). Vuetify's own `validate()` loop already knows how to apply a literal
 * (`const handler = typeof rule === 'function' ? rule : () => rule;`), so it must reach Vuetify as-is.
 */
function guardCallerRuleAgainstThrows(componentName: string, rule: FieldRuleFunction): AsyncFieldRuleFunction {
	return async (value) => {
		try {
			return await rule(value);
		} catch (error) {
			warnCallerRuleThrew(componentName, error);
			return true;
		}
	};
}

/** The diagnostic half of `guardCallerRuleAgainstThrows` above — see its doc comment for why this warns rather than fails the rule. */
function warnCallerRuleThrew(componentName: string, error: unknown): void {
	const reason = error instanceof Error ? error.message : String(error);
	console.warn(
		`[${componentName}] A caller-supplied validation rule threw and was treated as passed so the limit rule can still run: ${reason}. Fix the broken rule — it is not reporting real validation results.`
	);
}

/**
 * The structural surface of the underlying Vuetify field instance (`VTextField` or `VTextarea`) that
 * `useCharacterLimit` drives through a template ref. Both components' exposed instance types satisfy
 * this shape, so the composable stays generic-free — TypeScript widens the concrete instance type to
 * this interface at the call site instead.
 */
export interface CharacterLimitFieldInstance {
	isValid: boolean | null;
	validate: (silent?: boolean) => Promise<string[]>;
	resetValidation: () => Promise<void>;
	focus: () => void;
	blur: () => void;
}

/**
 * Builds a primitive fingerprint of a `rules` array, comparable by VALUE instead of by array
 * reference (LSP-16848 review finding F7). `:rules="[Validation.required]"` is an inline array
 * literal, so Vue hands this composable a brand-new array on every parent re-render even when its
 * contents — the same `Validation.required` function reference, in the same position — have not
 * actually changed. The revalidation `watch` below used to include the `mergedRules` computed itself
 * as a source, which compares that array by REFERENCE (Vue's default, non-deep `watch` comparison), so
 * an unrelated parent re-render re-invoked every caller rule — including a slow async one — through a
 * fresh `validate()`. Folding the array into a serialized tuple list here means two calls over
 * equivalent content produce the IDENTICAL string value, which Vue's watch then correctly treats as
 * unchanged; a genuine change — a different function, a different length, a swapped literal — still
 * produces a different string and still revalidates.
 *
 * A literal `string`/`boolean` rule entry is already a primitive, so it is embedded directly
 * (stored with its `typeof` so the literal `boolean` `true` and the literal `string` `"true"` cannot
 * collide). The signature uses `JSON.stringify` over token tuples instead of joining raw tokens with a
 * delimiter, so a literal containing that delimiter cannot collide with multiple separate literals. A
 * FUNCTION entry cannot be turned into a meaningful primitive by stringifying its source
 * (two textually-identical-but-distinct functions would wrongly collide, and a caller's rule may be a
 * closure whose source never changes even though what it closes over does) — `getRuleFunctionId` below
 * assigns each function reference a small stable integer instead.
 */
function buildRulesSignature(rules: FieldRule[] | undefined, getRuleFunctionId: (rule: FieldRuleFunction) => number): string {
	return JSON.stringify(
		(rules ?? []).map((rule) => (typeof rule === "function" ? ["fn", getRuleFunctionId(rule)] : ["lit", typeof rule, String(rule)]))
	);
}

/**
 * Shared engine of CharacterLimitedTextField / CharacterLimitedTextArea (LSP-16848). It is no longer
 * only a counter/rule pairing: it also owns the two wrappers' dev-time diagnostics, the revalidation
 * watch that keeps a stale verdict from surviving a prop change, the caller-`$attrs` filtering, and the
 * ref-based `validate`/`resetValidation`/`isValid`/`focus`/`blur` surface each SFC re-exposes — every
 * piece of `<script setup>` wiring that used to be duplicated line-for-line between the two components.
 *
 * At its core it still pairs a code-point-aware counter with a matching soft-overflow rule so the count
 * a user sees can never disagree with the verdict that blocks their save.
 *
 * Pass the component's display name (used in the diagnostic messages below), the template ref to the
 * underlying Vuetify field, and the component's reactive `props` object — the returned computeds track
 * `props`, and the returned functions read `fieldRef` lazily on each call.
 */
export function useCharacterLimit(componentName: string, fieldRef: TemplateRef<CharacterLimitFieldInstance>, props: CharacterLimitProps) {
	const mode = computed(() => (props.trim === false ? CHARACTER_LIMIT_MODES.untrimmed : CHARACTER_LIMIT_MODES.trimmed));

	/**
	 * Runs `props.countAs` (when supplied) over the value's text before it is counted or judged, so the
	 * counter and the limit rule measure the SAME string — see the prop's doc comment above.
	 *
	 * A value `stringifyForCounting` cannot read as text is handed on untouched, keeping the existing
	 * fail-closed path (`countCodePointsTrimmed`/`Untrimmed`'s `Infinity`) rather than turning it into an
	 * empty string that would count as 0. A transform that throws, or returns a non-string, falls back to
	 * the untransformed text: that is exactly the behaviour every field had before this prop existed, so a
	 * bug in a caller's transform can degrade the count back to the raw value but can never make the field
	 * unvalidatable or silently valid.
	 */
	const toCountedValue = (value: unknown): unknown => {
		const transform = props.countAs;
		if (typeof transform !== "function") {
			return value;
		}
		const stringified = stringifyForCounting(value);
		if (stringified === undefined) {
			return value;
		}
		try {
			const transformed = transform(stringified);
			return typeof transformed === "string" ? transformed : stringified;
		} catch {
			return stringified;
		}
	};

	const counterValue = computed(() => (value: unknown) => mode.value.countCharactersFor(toCountedValue(value)));

	/**
	 * The single numeric limit BOTH the counter's `:counter` max (bound in each SFC's template)
	 * and the limit rule below read, so they can never disagree about what a VALID `maxLength` means —
	 * the same "single-source" guarantee `counterValue`/`mode` above give `trim` (LSP-16848 break-it
	 * round 4). For a genuinely INVALID limit (0, negative, NaN, Infinity, fractional, non-numeric), this
	 * returns `0`: the invalid-limit branch below still fails closed for non-empty input, while every
	 * public consumer of `resolvedMaxLength.value` receives the `number` its type promises.
	 */
	const resolvedMaxLength = computed(() => (isInvalidMaxLength(props.maxLength) ? 0 : coerceMaxLength(props.maxLength)));

	// break-it round 4
	/**
	 * Live `aria-invalid` string for each SFC's template to bind on the inner Vuetify field
	 * (`CharacterLimitedTextField.vue` / `CharacterLimitedTextArea.vue` — LSP-16852/16853/16854/16855/
	 * 16859 break-it round 4). Soft overflow lets a user type past the limit — unlike the hard `maxlength`
	 * caps this epic replaced, where the browser itself refused the keystroke — so the save refusal that
	 * follows now needs an explicit announcement: nothing else in this stack sets `aria-invalid`
	 * (confirmed absent from Vuetify's `VField`, `VInput`, `VTextField`/`VTextarea`, and `useValidation` in
	 * `node_modules/vuetify/lib/composables/validation.js`), so a screen-reader user gets no signal that
	 * their save silently no-op'd.
	 *
	 * Reuses Vuetify's OWN validity, rather than recomputing it: `useValidation`'s `isValid` — `true` |
	 * `false` | `null` (not yet validated) — is returned from `VInput`'s/`VTextField`'s/`VTextarea`'s
	 * `setup()` with no `expose()` call restricting the public instance, so the whole return value,
	 * `isValid` included, reaches this template ref already reactive (Vue auto-unwraps the ref through the
	 * component's public proxy). Mirroring that value here means this can never disagree with the actual
	 * verdict blocking save — the same "single source of truth" discipline `resolvedMaxLength` above
	 * applies to the limit itself.
	 *
	 * A plain reactive `computed`, not the imperative `isValid()` FUNCTION returned below (that one is for
	 * a caller holding a ref outside a `v-form`, read on demand) — the template binding must update LIVE:
	 * `"true"` the moment a value goes over the limit, back to `"false"` the moment the user shortens it
	 * within the limit again. Deliberately does not read through `getMountedField` (which warns on a
	 * missing ref) — this computed evaluates on every render, including before the inner field mounts, and
	 * that is an expected, silent state here, not a diagnostic-worthy one.
	 */
	const ariaInvalid = computed(() => (fieldRef.value?.isValid === false ? "true" : "false"));

	/**
	 * The overflow rule's identity is STABLE — created once per component instance below, never a
	 * new closure every time `mergedRules` recomputes — and reads `props.maxLength` / `props.trim` /
	 * `props.overLimitMessage` (via `mode.value`, the SAME computed `counterValue` above reads, so trim
	 * is still branched on in exactly one place) AT INVOCATION TIME rather than at rule-creation
	 * time.
	 *
	 * This matters because Vuetify's own `validate()` loop (`node_modules/vuetify/lib/composables/
	 * validation.js`) takes a snapshot of the rules array ONCE, at the start of its `for...of`, and
	 * keeps iterating that same snapshot even if the field's `rules` prop is reassigned to a new
	 * array while an earlier rule in it — a caller's slow async rule, e.g. a server-side uniqueness
	 * check — is still being awaited. Under the round-1 shape (`createLimitRule(...)` called fresh
	 * inside the `mergedRules` computed), the rule sitting in that stale snapshot would have closed
	 * over whatever `maxLength`/`trim`/`overLimitMessage` were current at the moment the snapshot was
	 * taken, so a validation that started before a prop change and settles after it would judge the
	 * OLD limit forever — overwriting the correct verdict with a stale one. Because `limitRule` below
	 * is the exact same function reference sitting in that stale snapshot, invoking it later still
	 * reads whatever is CURRENT (LSP-16848 break-it round 2).
	 */
	const limitRule: FieldRuleFunction = (value) => {
		// `resolveOverflowMessage` (not a bare `props.overLimitMessage ?? default`) guarantees this is
		// always a non-empty STRING before it can reach Vuetify's own `validate()` loop as the "invalid"
		// return value — see its doc comment for why a raw non-string `overLimitMessage` would otherwise let
		// an overflowing value silently validate as saveable (LSP-16848 break-it round 5).
		const countedValue = toCountedValue(value);
		// The MESSAGE is built from the untransformed value on purpose: its emoji/accent hint explains why
		// the user's own text costs more than it looks like it should, and a transformed stand-in string is
		// not the user's text. Only the VERDICT below measures the transformed value.
		const overflowMessage = resolveOverflowMessage(props.overLimitMessage, resolvedMaxLength.value, props.fieldNoun, value);

		// `isWithinCharacterLimit` owns the actual counting/threshold decision, including the fail-closed
		// "only an EMPTY value is valid for an invalid limit" handling (Required is the caller's own
		// rule, not ours) — see its doc comment above. `resolvedMaxLength.value` is already the
		// invalid-aware coerced limit (`0` when `props.maxLength` itself is unusable), and `0` re-triggers
		// the SAME invalid-limit branch inside `isWithinCharacterLimit`, so this stays the same rule the
		// inlined version above used to compute.
		return (
			isWithinCharacterLimit(countedValue, resolvedMaxLength.value, mode.value === CHARACTER_LIMIT_MODES.trimmed) || overflowMessage
		);
	};

	// Not annotated a single element type: the array mixes the sync `limitRule`, the always-async
	// throw-guarded wrapper (for FUNCTION caller rules only), and any LITERAL caller rule passed through
	// unchanged — `FieldRuleFunction | AsyncFieldRuleFunction | boolean | string`, matching what
	// Vuetify's own `rules` prop type (`ValidationRule` in `node_modules/vuetify/lib/composables/
	// validation.d.ts`) accepts per element.
	//
	// A LITERAL rule (`typeof rule !== "function"`) is passed through UNCHANGED rather than wrapped:
	// Vuetify's own `validate()` loop already knows how to apply it (`const handler = typeof rule ===
	// 'function' ? rule : () => rule;`), whereas wrapping it would have `guardCallerRuleAgainstThrows`
	// *call* the literal, throw, and report it as passing — silently discarding the caller's rule
	// (LSP-16848 break-it round 4).
	const mergedRules = computed(() => [
		...(props.rules ?? []).map((rule) => (typeof rule === "function" ? guardCallerRuleAgainstThrows(componentName, rule) : rule)),
		limitRule
	]);

	// Backs `buildRulesSignature`'s per-function ids below. Scoped to this component INSTANCE (not
	// module-level): a fresh `WeakMap` per `useCharacterLimit()` call means ids never accumulate across
	// remounts, and — since ids only need to be stable within one instance's own successive
	// signature comparisons, never compared across different fields — there is no reason to share it.
	const ruleFunctionIds = new WeakMap<FieldRuleFunction, number>();
	let nextRuleFunctionId = 0;
	function getRuleFunctionId(rule: FieldRuleFunction): number {
		const existingId = ruleFunctionIds.get(rule);
		if (existingId !== undefined) {
			return existingId;
		}
		const newId = nextRuleFunctionId++;
		ruleFunctionIds.set(rule, newId);
		return newId;
	}

	// See `buildRulesSignature`'s doc comment above for why the revalidation watch below sources THIS
	// (a primitive string, compared by value) rather than `mergedRules` (an array, compared by
	// reference) or `props.rules` itself (LSP-16848 review finding F7).
	const rulesSignature = computed(() => buildRulesSignature(props.rules, getRuleFunctionId));

	// inheritAttrs must stay false on the calling component: we bind a FILTERED copy of $attrs below,
	// and Vue would otherwise auto-apply the unfiltered set to the root element and put a caller's
	// maxlength back. (Each SFC still declares `defineOptions({ inheritAttrs: false })` itself — that
	// option is compiled per-component and cannot be set from a composable.)
	const attrs = useAttrs();
	const passthroughAttrs = computed(() => sanitizeWrapperOwnedAttrs(attrs));
	const hasExplicitValidationRequest = ref(false);

	const parentForm = inject(FormKey, null);
	if (parentForm) {
		const formRegistrationProxy: FormProvide = {
			...parentForm,
			register: (item: Parameters<FormProvide["register"]>[0]) => {
				parentForm.register({
					...item,
					validate: async () => {
						hasExplicitValidationRequest.value = true;
						return item.validate();
					},
					reset: async () => {
						hasExplicitValidationRequest.value = false;
						return item.reset();
					},
					resetValidation: async () => {
						hasExplicitValidationRequest.value = false;
						return item.resetValidation();
					}
				});
			}
		};

		provide(FormKey, formRegistrationProxy);
	}

	function validate(silent?: boolean): Promise<string[]> {
		hasExplicitValidationRequest.value = true;
		const field = getMountedField("validate");
		if (!field) {
			return Promise.resolve([buildUnavailableFieldMessage(componentName, "validate")]);
		}
		return field.validate(silent);
	}

	function resetValidation(): Promise<void> {
		hasExplicitValidationRequest.value = false;
		return getMountedField("resetValidation")?.resetValidation() ?? Promise.resolve();
	}

	async function revalidateAfterInnerFieldPatch(): Promise<void> {
		await nextTick();
		await fieldRef.value?.validate();
	}

	// Reactive, not a one-shot setup-time call: a parent can flip hide-details (or maxLength) to a
	// suppressing/invalid value after mount, and this must catch that too (LSP-16848 break-it round 1).
	// `useAttrs()` tracks every attr read through a single dependency bucket in this Vue version, so a
	// broad `watchEffect` would re-run on each `modelValue` update. Use explicit watch sources instead:
	// Vue may re-evaluate the getters on any attrs update, but the callback only fires when the two
	// hide-details spellings actually change.
	watch([() => attrs["hide-details"], () => attrs.hideDetails], () => warnIfHideDetailsHidesCounter(componentName, attrs), {
		immediate: true
	});
	watchEffect(() => warnIfMaxLengthIsInvalid(componentName, props.maxLength));
	watchEffect(() => warnIfOverLimitMessageIsUnusable(componentName, props.overLimitMessage, resolvedMaxLength.value, props.fieldNoun));

	// R3: Vuetify only re-runs validate() from its own watchers on the model and on focus — never when
	// `rules` changes, so a lowered maxLength (or a swapped rules/overLimitMessage/trim) would otherwise
	// leave a stale verdict for a caller that already asked this wrapper to validate (LSP-16848 break-it
	// round 1). The flag is set both by direct wrapper-ref `validate()` and by parent `v-form.validate()`
	// through the form registration proxy above; Vuetify's silent default `validate-on` mount pass still
	// bypasses it, so untouched fields do not get forced into visible errors by prop churn.
	//
	// Watch sources are explicit (`maxLength`/`trim`/`overLimitMessage`/`fieldNoun` alongside
	// `rulesSignature`), not just `mergedRules` alone (break-it round 2): now that `limitRule` above has
	// a STABLE identity — needed so a stale in-flight invocation still reads current props —
	// `mergedRules` only gains a new array reference when `props.rules` itself changes. A
	// `maxLength`/`trim`/`overLimitMessage`/`fieldNoun`-only change no longer touches `mergedRules`'s
	// identity, so watching those four directly is what keeps the round-1 guarantee (an already-touched
	// field revalidates when any of them changes) intact under the round-2 rule shape. `fieldNoun` feeds
	// the rendered overflow message the same way `overLimitMessage` does (review finding) — every
	// existing call site passes it as a static template attribute today, but nothing stops a future
	// caller from binding it reactively, and an already-invalid field must not show a stale message.
	//
	// `rulesSignature` (a primitive string), not `mergedRules` (an array) or `props.rules` itself, is
	// the fourth source — LSP-16848 review finding F7: `:rules="[Validation.required]"` is an inline
	// array literal, so an unrelated parent re-render hands this composable a brand-new `props.rules`
	// array (and therefore a brand-new `mergedRules` array) on every render even when the actual rule
	// FUNCTIONS inside it have not changed. Watching either array by its default, non-deep, by-reference
	// comparison would re-`validate()` — and so re-invoke every caller rule, including a slow async one —
	// on every such re-render. `rulesSignature` folds the array down to a value that compares by VALUE
	// instead, so an equivalent inline array leaves it unchanged while a genuine rule swap still changes
	// it. See `buildRulesSignature`'s doc comment above for the full mechanism.
	watch(
		[rulesSignature, () => props.maxLength, () => props.trim, () => props.overLimitMessage, () => props.fieldNoun, () => props.countAs],
		() => {
			if (fieldRef.value && hasExplicitValidationRequest.value) {
				void revalidateAfterInnerFieldPatch();
			}
		},
		// `flush: "post"` is load-bearing: the default "pre" flush runs BEFORE the inner Vuetify field's
		// own `rules` prop has actually received the new `mergedRules` array (that happens during the
		// render patch), so a "pre" validate() call would still see the OLD rules and produce a stale
		// verdict. Waiting for "post" guarantees the inner field's `rules` are current first.
		{ flush: "post" }
	);

	return {
		passthroughAttrs,
		counterValue,
		resolvedMaxLength,
		ariaInvalid,
		mergedRules,
		// Ref-based validation and focus, for callers holding a ref instead of going through a v-form.
		// Vue's expose proxy does not forward the inner component's own methods, so each one needed by a
		// caller has to be re-exposed explicitly — the SFCs' own `defineExpose` blocks forward these.
		validate,
		resetValidation,
		isValid: () => getMountedField("isValid")?.isValid ?? null,
		focus: () => getMountedField("focus")?.focus(),
		blur: () => getMountedField("blur")?.blur()
	};

	function getMountedField(methodName: string): CharacterLimitFieldInstance | undefined {
		const field = fieldRef.value;
		if (!field) {
			warnFieldRefUnavailable(componentName, methodName);
			return undefined;
		}
		return field;
	}
}

function buildUnavailableFieldMessage(componentName: string, methodName: string): string {
	return `[${componentName}] ${methodName}() was called while the inner Vuetify field is not mounted.`;
}

function warnFieldRefUnavailable(componentName: string, methodName: string): void {
	console.warn(
		`${buildUnavailableFieldMessage(componentName, methodName)} The exposed ref method cannot run until the wrapper has mounted its inner field.`
	);
}

/**
 * Strips Vue's forced-binding-modifier prefix from an `$attrs` key before every comparison below, so
 * `:maxlength.attr="5"` / `:value.prop="'x'"` (and the same modifiers on `validation-value`/
 * `validationValue`/`max-errors`/`maxErrors`) are caught exactly like their unmodified spellings.
 *
 * `.attr` and `.prop` do not just change how Vue's runtime DOM patcher (`node_modules/@vue/runtime-dom/
 * dist/runtime-dom.cjs.js`) applies the value once it reaches a native element — they change the KEY
 * itself as seen by `$attrs`, at compile time, well before this filter ever runs: `:maxlength.attr`
 * arrives keyed as `^maxlength`, `:maxlength.prop` as `.maxlength`. Every attribute name this file
 * cares about is otherwise matched with a plain `attributeName.toLowerCase() === "maxlength"`-style
 * compare (see `NATIVE_MAX_LENGTH_ATTRIBUTE_NAME`, `NATIVE_VALUE_ATTRIBUTE_NAME`,
 * `VALIDATION_VALUE_ATTRIBUTE_NAMES`, `MAX_ERRORS_ATTRIBUTE_NAMES` below) — none of which can ever equal
 * `"^maxlength"` or `".maxlength"`, so an unnormalized compare lets either modifier sail straight past
 * `sanitizeWrapperOwnedAttrs` and reach the DOM as a real native `maxlength`/`value`, reinstating the
 * hard cap (or the display/model desync) this component family exists to prevent. Do NOT delete this
 * normalization step as dead-looking indirection — without it, `sanitizeWrapperOwnedAttrs` silently
 * stops working for anything spelled with `.attr`/`.prop`.
 *
 * `^`/`.` can only ever appear as the FIRST character of a compiler-emitted key, never elsewhere in a
 * legitimate attribute name, so anchoring the strip to `^` in the regex (not a global replace) is safe.
 */
function stripForcedBindingModifierPrefix(attributeName: string): string {
	return attributeName.replace(/^[\^.]/, "");
}

/** Only the all-lowercase spelling can arrive here: `:max-length` / `:maxLength` bind the declared `maxLength` prop instead. */
const NATIVE_MAX_LENGTH_ATTRIBUTE_NAME = "maxlength";

/**
 * `validationValue` is not a declared prop on either wrapper, so — like `hide-details` below — it can
 * arrive spelled either way a caller wrote it: kebab-case `validation-value` (a literal template
 * attribute) or camelCase `validationValue` (an object-spread `v-bind`). Both are stripped.
 */
const VALIDATION_VALUE_ATTRIBUTE_NAMES = ["validation-value", "validationValue"] as const;

/**
 * Native `value` is, like `maxlength` above, not a declared prop on either wrapper — the wrapper's
 * model prop is `modelValue` (bound via `v-model` or the explicit `:model-value`/`@update:model-value`
 * form), not `value`. So a caller's literal `value="..."` template attribute, or a `value` key in a
 * spread `$attrs` object, lands here rather than on a declared prop, and — because it is a REAL
 * Vuetify passthrough attr, not a component prop — Vuetify's own `filterInputAttrs` (`node_modules/
 * vuetify/lib/composables/component.js`) routes it straight onto the native `<input>`/`<textarea>`
 * element, independently of `modelValue`. That desyncs what the user sees from what the counter counts
 * and the limit rule validates — the exact "display disagrees with the model" failure this component
 * family exists to prevent (LSP-16848 break-it round 6), so it must never reach the inner field.
 *
 * `value` is a single word, so — unlike `validationValue`/`hide-details` above, which need a kebab AND
 * a camel entry — there is no separate kebab-case spelling to also list: a literal template attribute
 * and a camelCase attrs key look identical (`"value"`). Checked case-insensitively below, the same
 * belt-and-suspenders treatment `maxlength` gets, in case a caller writes an unusual casing.
 *
 * Must NOT match `modelValue` / `model-value` — those spellings are the model itself (still not a
 * declared prop on either wrapper, so they also flow through this same `$attrs` passthrough), and
 * stripping them would break `v-model` and the explicit `:model-value`/`@update:model-value` form
 * alike. The exact-string compare below (`attributeName.toLowerCase() === NATIVE_VALUE_ATTRIBUTE_NAME`)
 * cannot match either spelling of the model prop — `"modelvalue"` and `"model-value"` are both
 * different strings from `"value"`.
 */
const NATIVE_VALUE_ATTRIBUTE_NAME = "value";

/**
 * `max-errors` / `maxErrors` is not a declared prop on either wrapper either, so — like
 * `hide-details` / `validationValue` above — it can arrive spelled either way a caller wrote it.
 * Unlike those two, it is not stripped: a caller legitimately setting `max-errors="3"` to show three
 * messages at once must keep working. Only clamped when it would disable rule validation entirely
 * (see `isBelowMinimumMaxErrors` below).
 */
const MAX_ERRORS_ATTRIBUTE_NAMES = ["max-errors", "maxErrors"] as const;

/**
 * `true` when this `max-errors` value would stop Vuetify's own `validate()` loop
 * (`node_modules/vuetify/lib/composables/validation.js`) before it runs a single rule:
 * `for (const rule of rules.value) { if (results.length >= Number(props.maxErrors ?? 1)) break;
 * ... }`. `results.length` starts at `0`, so any value that does not coerce to `>= 1` — `0`, a
 * negative number, `NaN` from junk like `"abc"`, `undefined` passed explicitly — makes that
 * condition true on the very first iteration, breaking out before even OUR OWN limit rule runs.
 * That would let an overflowing field validate as saveable, taking the parent `v-form` with it
 * (LSP-16848 break-it round 2). `>= 1` (not `> 0`) also catches `NaN`, since every comparison with
 * `NaN` is `false` — `!(NaN >= 1)` is `true` — which additionally keeps a caller's typo from
 * silently switching Vuetify into "show unlimited error messages" instead of its documented
 * single-message default.
 */
function isBelowMinimumMaxErrors(value: unknown): boolean {
	return !(Number(value) >= 1);
}

/**
 * @internal Exported only for the focused sanitizer regression tests; app code should use
 * `useCharacterLimit`, which supplies the live Vue `$attrs` proxy and reactive wiring.
 *
 * Strips or clamps every attribute the wrapper has an opinion about out of the attributes a caller
 * passed through, so a caller's copy can never fight the wrapper's own explicit bindings, and can
 * never silently disable the wrapper's own limit rule. Three kinds of thing get removed outright
 * (native `maxlength`, `validation-value`/`validationValue`, native `value`) and one gets clamped
 * rather than removed (`max-errors`/`maxErrors`):
 *
 * - Native `maxlength` — soft overflow means typing past the limit is allowed, so it must never reach
 *   the input. It would both hard-cap keystrokes in UTF-16 code units (cutting an emoji mid-cluster)
 *   and hijack the counter's max, since Vuetify's text field and textarea both prefer
 *   `attrs.maxlength` over `:counter`.
 * - `validation-value` / `validationValue` — Vuetify's `validationValue` prop makes rules validate
 *   that value instead of the model (`validation.js`'s `validationModel` computed), while the counter
 *   always counts the real model. Left unstripped, a caller could show a red over-limit counter next
 *   to a form that reports valid — the exact counter-disagrees-with-rule failure this component
 *   family exists to prevent (LSP-16848).
 * - Native `value` — not a Vuetify prop on either inner field (their model prop is `modelValue`), so
 *   Vuetify's own `filterInputAttrs` routes it straight onto the native `<input>`/`<textarea>` element,
 *   independently of `modelValue`. Left unstripped, the DOM could display a string the counter and the
 *   limit rule never see — the same counter-disagrees-with-rule failure as `validation-value` above,
 *   just reached through the display instead of through validation (LSP-16848 break-it round 6). See
 *   `NATIVE_VALUE_ATTRIBUTE_NAME` above for why `modelValue`/`model-value` are unaffected.
 * - `max-errors` / `maxErrors` — floored to `1` (Vuetify's own declared default) when it would
 *   otherwise disable rule validation entirely, per `isBelowMinimumMaxErrors` above. A caller value
 *   that is already `>= 1` passes through untouched, so `max-errors="3"` keeps showing three
 *   messages.
 *
 * Every comparison below runs on the attribute name AFTER `stripForcedBindingModifierPrefix` above has
 * removed a leading `^`/`.` — see that function's doc comment for why a forced-binding modifier
 * (`:maxlength.attr`/`:maxlength.prop`, and the same on the other three attribute names this function
 * owns) would otherwise slip past a plain lowercase compare (LSP-16848).
 */
export function sanitizeWrapperOwnedAttrs(attrs: Record<string, unknown>): Record<string, unknown> {
	// MUST iterate `Object.entries(attrs)` here, not `Object.keys(attrs)` — this reads every VALUE
	// through the proxy's tracked `get`, not just its key names. `useAttrs()`'s proxy
	// (`attrsProxyHandlers` in the installed `@vue/runtime-core`) only traps `get` / `set` /
	// `deleteProperty`; it defines no `ownKeys` trap, so a `get`-less scan (`Object.keys` alone, or
	// `for...in`) never calls `track()` on anything. Combined with this function's caller wrapping it
	// in a `computed`, that means a `Object.keys`-only version would never subscribe the computed to
	// `attrs` at all — on the common path (no blocked attribute present, the overwhelming majority of
	// calls) it would never even evaluate `attrs[someKey]`, so the computed's dependency set stays
	// empty and it never recomputes. Vue still forces this component to re-render when a parent's own
	// re-render changes its attrs, but that re-render reads the STALE cached computed value, which
	// happens to be the SAME live `attrs` proxy reference returned unchanged at mount — so a caller
	// that adds `maxlength`/`value`/`validation-value` to that proxy after mount would flow straight
	// through the stale `v-bind` untouched by this filter, reinstating the hard cap this component
	// exists to prevent (LSP-16848 review finding F6). Do NOT restore a `Object.keys`-only scan as a
	// "read fewer properties" optimization — correctness requires reading every value, every call, so
	// the computed's dependency tracking actually sees `attrs` change.
	const ownedAttributeNamesToStrip: string[] = [];
	// Unlike a canonical-spelling scan, this collects the CALLER's actual attrs keys (not the two
	// canonical `MAX_ERRORS_ATTRIBUTE_NAMES` spellings) so the floored value is written back onto the
	// exact key present — `^max-errors` or `.maxErrors` included — rather than onto a canonical
	// spelling that was never actually there.
	const maxErrorsAttributeNamesToFloor: string[] = [];
	for (const [attributeName, attributeValue] of Object.entries(attrs)) {
		const normalizedAttributeName = stripForcedBindingModifierPrefix(attributeName);
		if (
			normalizedAttributeName.toLowerCase() === NATIVE_MAX_LENGTH_ATTRIBUTE_NAME ||
			normalizedAttributeName.toLowerCase() === NATIVE_VALUE_ATTRIBUTE_NAME ||
			(VALIDATION_VALUE_ATTRIBUTE_NAMES as readonly string[]).includes(normalizedAttributeName)
		) {
			ownedAttributeNamesToStrip.push(attributeName);
			continue;
		}
		if (
			(MAX_ERRORS_ATTRIBUTE_NAMES as readonly string[]).includes(normalizedAttributeName) &&
			isBelowMinimumMaxErrors(attributeValue)
		) {
			maxErrorsAttributeNamesToFloor.push(attributeName);
		}
	}

	// Every value was already read above (needed for tracking — see the comment on the loop), so
	// there is no cheaper "did anything change" check left to make; only the copy-and-mutate below is
	// worth skipping on the common path.
	if (ownedAttributeNamesToStrip.length === 0 && maxErrorsAttributeNamesToFloor.length === 0) {
		return attrs;
	}

	const sanitizedAttrs = { ...attrs };
	for (const attributeName of ownedAttributeNamesToStrip) {
		delete sanitizedAttrs[attributeName];
	}
	for (const attributeName of maxErrorsAttributeNamesToFloor) {
		sanitizedAttrs[attributeName] = 1;
	}
	return sanitizedAttrs;
}

/**
 * `hide-details` is not a declared prop on either wrapper, so — unlike `maxLength` above, which Vue
 * routes to the prop and keeps out of `$attrs` — it can reach `$attrs` spelled either way a caller
 * wrote it: kebab-case `hide-details` (what a template literally emits) or camelCase `hideDetails`
 * (an object-spread `v-bind`). Both are checked.
 */
const HIDE_DETAILS_ATTRIBUTE_NAMES = ["hide-details", "hideDetails"] as const;

/** `true` when this `hide-details` value would suppress Vuetify's `details` slot (Vuetify's `VInput` and its `hasDetails` computed). */
function suppressesDetailsSlot(value: unknown): boolean {
	// A caller who writes the BARE attribute — `<CharacterLimitedTextField hide-details />`, exactly
	// what the Contact (Optional) field in ActionVideo.vue / ActionSchedule.vue does today — never
	// produces a boolean `true` here. Vue's template compiler emits the empty string `""` for a
	// valueless attribute, and VTextField / VTextarea declare `hideDetails: [Boolean, String]`
	// (Boolean first), so it is Vue's own prop-cast — not this function — that turns `""` (or the
	// literal string `"hide-details"`, the hyphenated prop name) into `true`: Vue's `resolvePropValue`
	// and its `shouldCastTrue` branch — `value === "" || value === hyphenate(key)`. A plain
	// `Boolean(value)` check misses this, the most common real-world spelling, so both are
	// special-cased here before the general truthy check.
	if (value === "" || value === "hide-details") {
		return true;
	}

	// This must mirror Vuetify's own `hasDetails` gate EXACTLY — `!props.hideDetails ||
	// props.hideDetails === "auto" && (...)` (Vuetify's `VInput` and its `hasDetails` computed) —
	// rather than guess at author intent. Boolean false, undefined, 0, etc. are falsy to that gate, so
	// they leave hide-details unset/off — no suppression. "auto" defers to Vuetify's own
	// has-messages/has-slot check. The non-empty string "false" is NOT the boolean false: it is truthy,
	// and Vuetify's gate does not special-case it, so it DOES suppress the details slot no matter what
	// the caller meant by writing it — this must warn, not be treated as "off" (LSP-16848 break-it round 1).
	return Boolean(value) && value !== "auto";
}

/**
 * Warns, in dev, when a caller's `hide-details` on a CharacterLimited* field would silently drop the
 * character counter from the DOM (LSP-16848). Vuetify only renders the counter inside the `details`
 * slot, which it gates on `hide-details` being falsy or `"auto"` — a bare `hide-details` (`true`)
 * removes the counter's markup entirely, not just its visibility.
 *
 * Deliberately does NOT coerce or override the caller's value: that would newly surface validation
 * error text under fields whose compact layout depended on `hide-details`. This is a diagnostic only,
 * so a caller (or a mechanical migration such as LSP-16854) notices before shipping a counter-less
 * field, per root CLAUDE.md's "Diagnostic logging when fixing silent failures".
 *
 * Pure and synchronous. Callers should watch the two hide-details attrs explicitly instead of wrapping
 * this in a broad `watchEffect`: Vue 3's `$attrs` proxy tracks attrs through a shared dependency bucket
 * in this repo's installed version, so a broad effect would also re-run on unrelated attrs such as
 * `modelValue` updates.
 *
 * Dev-only (LSP-16852/16853/16854/16855/16859 review), unlike `warnIfMaxLengthIsInvalid`/
 * `warnIfOverLimitMessageIsUnusable` below: this one is a lint-style warning about a CALLER MISTAKE,
 * not an incident breadcrumb a production triage needs. `AddComment.vue`'s `hideCounter` computed
 * legitimately returns a suppressing value on every idle, unfocused mount (hide-until-engaged,
 * LSP-16404/16405) — that is intended behavior, not a caller bug — so an ungated warn here fires on
 * every real user's every comment-box mount and gets forwarded to Datadog at full session sample rate
 * (`plugins/datadog.ts`'s `forwardConsoleLogs`): pure noise, not signal. A developer authoring a NEW
 * caller still sees this locally (`import.meta.env.DEV` is true under `npm run dev` and under Vitest),
 * which is the only audience that can actually act on it.
 */
function warnIfHideDetailsHidesCounter(componentName: string, attrs: Record<string, unknown> | undefined): void {
	if (!attrs || !import.meta.env.DEV) {
		return;
	}

	for (const attributeName of HIDE_DETAILS_ATTRIBUTE_NAMES) {
		if (suppressesDetailsSlot(attrs[attributeName])) {
			// Describe what the caller did and what happens, not "truthy"/"auto" internals — a caller
			// who wrote the bare `hide-details` shorthand (the most common case, see
			// suppressesDetailsSlot above) never set anything they'd recognize as "truthy".
			console.warn(
				`[${componentName}] hide-details is set on this field, so its character counter will not render at all (its markup is removed, not just hidden). Remove hide-details, or set it to "auto", to keep the counter visible.`
			);
			return;
		}
	}
}

/**
 * Coerces `maxLength` to a plain number so a STATIC template attribute written without a binding
 * colon — `max-length="3"`, natural and common, and exactly what `<CharacterLimitedTextField
 * max-length="3" />` writes — is judged and used identically to `:max-length="3"` (LSP-16848 break-it
 * round 4). Vue does not auto-cast a `number`-typed prop the way it does `Boolean`, so that attribute
 * arrives as the STRING `"3"` at runtime despite `CharacterLimitProps.maxLength` being typed `number`.
 *
 * `Number(...)`, not `parseInt`/`parseFloat`: a partially-numeric string like `"3abc"` must NOT
 * silently parse to `3` — the whole string has to be numeric, or it stays unusable. Anything that is
 * neither a `number` nor a `string` (`null`, `undefined`, `boolean`, objects) returns `NaN`, which
 * `isInvalidMaxLength` below already treats as invalid via `Number.isInteger`.
 */
function coerceMaxLength(maxLength: unknown): number {
	return typeof maxLength === "number" || typeof maxLength === "string" ? Number(maxLength) : NaN;
}

/**
 * `true` when the COERCED `maxLength` (see `coerceMaxLength` above) is not a usable positive integer
 * limit — `0`, negative, `NaN`, `Infinity`, fractional, or a non-numeric value/string. The prop doc
 * states it is expected to be at least 1 (code points). Coercing first means a valid numeric STRING
 * (`"3"`) is judged exactly like the bound number `3`, while a genuinely unusable value — including a
 * non-numeric string like `"abc"` — still fails closed exactly as before.
 */
function isInvalidMaxLength(maxLength: unknown): boolean {
	const coercedMaxLength = coerceMaxLength(maxLength);
	return !Number.isInteger(coercedMaxLength) || coercedMaxLength < 1;
}

/**
 * Warns, in dev, when a caller's `maxLength` on a CharacterLimited* field is not a usable positive
 * integer (LSP-16848 break-it round 1). Vuetify's `VCounter` renders no `/ max` segment for a falsy
 * `max` (`props.max ? \`${value} / ${max}\` : String(value)`), so `0` silently degrades to a bare
 * running count with no visible ceiling, while the paired limit rule still runs and rejects every
 * non-empty value — a mismatch between "no visible limit" and "nothing is ever valid" that is worse
 * than either behaviour alone. Negative/`NaN`/fractional values are equally unusable.
 *
 * Diagnostic only — never CLAMPS the caller's value, and the message below still echoes whatever raw
 * value was passed in, per root CLAUDE.md's "Diagnostic logging when fixing silent failures".
 * `isInvalidMaxLength` internally coerces a numeric STRING (e.g. `"3"`) before judging it (LSP-16848
 * break-it round 4), so this deliberately does NOT warn for one — only a value that is still unusable
 * after coercion reaches this branch. Pure and synchronous like `warnIfHideDetailsHidesCounter` above,
 * so a caller wraps it in `watchEffect(() => warnIfMaxLengthIsInvalid(...))` to also catch a
 * `maxLength` that becomes invalid after mount. Typed `number | string` to match the widened
 * `CharacterLimitProps.maxLength` (LSP-16848 review finding F3) — the message below interpolates
 * either shape fine, and `isInvalidMaxLength` already accepts `unknown`.
 */
function warnIfMaxLengthIsInvalid(componentName: string, maxLength: number | string): void {
	if (isInvalidMaxLength(maxLength)) {
		console.warn(
			`[${componentName}] maxLength must be a positive integer (received ${maxLength}); the counter will show no maximum and the limit rule will reject every non-empty value.`
		);
	}
}

/**
 * The built-in overflow copy `limitRule` falls back to whenever `overLimitMessage` is absent or
 * unusable. Without a `fieldNoun`, stays the generic "Must not exceed N characters." — every existing
 * call site keeps its current copy until it opts in. With one, names the field. Emoji and accented text
 * also get a short caveat because their rendered/storage footprint can vary outside this code-point
 * limit (for example, a decomposed accent contains a combining mark and a ZWJ emoji is several code
 * points), even when the field's actual threshold remains correct.
 */
/**
 * `true` for a string with at least one non-whitespace character. Vuetify's own `validate()` loop
 * (`node_modules/vuetify/lib/composables/validation.js`) does not reject an empty/blank string the
 * way it rejects a non-string result — `results.push(result || '')` still marks the field invalid —
 * but an empty message renders as no visible text at all, which is exactly as useless to the user as
 * the field silently validating: either way there is nothing telling them why the field is red
 * (LSP-16848 break-it round 5). `resolveOverflowMessage` below treats a blank string the same as an
 * unusable one for that reason.
 */
function isUsableMessageText(value: string): boolean {
	return value.trim().length > 0;
}

/**
 * `true` when `value`'s `toString` is something OTHER than the inherited `Object.prototype.toString`
 * every plain object (`{}`, `Object.create({})`, a bare object literal) gets for free — the giveaway
 * that coercing `value` via `String(...)` would produce the human-meaningless literal `"[object
 * Object]"` rather than text the caller actually intended. A `String` wrapper, a `Date`, an `Array`,
 * or a class instance with its own `toString` override all report `true` here; a plain object
 * reports `false`. `typeof value.toString === "function"` guards `Object.create(null)`, which has no
 * prototype chain at all and would otherwise throw calling `.toString` on the next line.
 */
function hasOwnToString(value: object): boolean {
	const toString = (value as { toString?: unknown }).toString;
	return typeof toString === "function" && toString !== Object.prototype.toString;
}

/**
 * Coerces a caller's `overLimitMessage` to a usable string, or returns `undefined` when it cannot be —
 * the signal `resolveOverflowMessage` below uses to fall back to the built-in default instead
 * (LSP-16848 break-it round 5). This exists because `limitRule` hands its return value STRAIGHT to
 * Vuetify's own `validate()` loop as the rule's "invalid" result, and that loop discards the WHOLE
 * rule — as if it had never run — for any result that is neither `false` nor a `string`:
 *   `if (result !== false && typeof result !== 'string') { console.warn(...); continue; }`
 * A caller who passes a `number` (a common typo/mutation mistake), or whose `overLimitMessage` prop
 * mutates to a non-string value after mount, would otherwise let an overflowing value silently
 * validate as SAVEABLE while the counter still shows red — the exact counter-disagrees-with-rule
 * failure this component family exists to prevent.
 *
 * - A `string` is returned as-is if `isUsableMessageText` above accepts it; a blank one is
 *   deliberately treated as unusable (see that function's doc comment).
 * - A finite `number` (not `NaN`) is coerced via `String(...)` — `0` becomes `"0"`. That is not a
 *   helpful message, but it IS the string the caller explicitly (if mistakenly) chose to pass, and
 *   it still reaches Vuetify as a valid, non-empty result.
 * - An object with a real `toString` (`hasOwnToString` above) is coerced via `String(...)`, provided
 *   the result is itself usable text — this is what lets a `String` wrapper (`new String("...")`)
 *   round-trip to its original text instead of falling back.
 * - Everything else — `null`, `undefined`, `NaN`, a `boolean`, a plain object with no overridden
 *   `toString` (which would otherwise coerce to the useless `"[object Object]"`) — returns
 *   `undefined`.
 */
function coerceOverLimitMessage(overLimitMessage: unknown): string | undefined {
	if (typeof overLimitMessage === "string") {
		return isUsableMessageText(overLimitMessage) ? overLimitMessage : undefined;
	}

	if (typeof overLimitMessage === "number") {
		return Number.isNaN(overLimitMessage) ? undefined : String(overLimitMessage);
	}

	if (typeof overLimitMessage === "object" && overLimitMessage !== null && hasOwnToString(overLimitMessage)) {
		let coerced: string;
		try {
			coerced = String(overLimitMessage);
		} catch {
			return undefined;
		}
		return isUsableMessageText(coerced) ? coerced : undefined;
	}

	return undefined;
}

/**
 * The single source `limitRule` calls to turn `props.overLimitMessage` into the overflow message it
 * hands to Vuetify — coerced via `coerceOverLimitMessage` above when possible, or the built-in default
 * when not. Guarantees the result is ALWAYS a non-empty string, so the limit rule can never degrade
 * into a silently-skipped rule no matter what shape `overLimitMessage` arrives in (LSP-16848 break-it
 * round 5).
 */
function resolveOverflowMessage(overLimitMessage: unknown, maxLength: number, fieldNoun: string | undefined, value: unknown): string {
	return coerceOverLimitMessage(overLimitMessage) ?? defaultOverflowMessage(maxLength, fieldNoun, value);
}

/**
 * Warns, in dev, when a caller's `overLimitMessage` on a CharacterLimited* field could not reach Vuetify
 * as the exact string the caller intended (LSP-16848 break-it round 5) — either because
 * `resolveOverflowMessage` had to coerce a non-string value, or because it fell all the way back to
 * the built-in default copy. Skipped for the two ordinary cases so a normal caller never sees this:
 * `overLimitMessage` left at its `undefined` default, and an `overLimitMessage` that is already a non-empty
 * string. Pure and synchronous like `warnIfMaxLengthIsInvalid` above, so a caller wraps it in
 * `watchEffect(() => warnIfOverLimitMessageIsUnusable(...))` to also catch an `overLimitMessage` that mutates
 * to an unusable value after mount.
 */
function warnIfOverLimitMessageIsUnusable(
	componentName: string,
	overLimitMessage: unknown,
	resolvedMaxLengthValue: number,
	fieldNoun: string | undefined
): void {
	if (overLimitMessage === undefined || (typeof overLimitMessage === "string" && isUsableMessageText(overLimitMessage))) {
		return;
	}

	const coerced = coerceOverLimitMessage(overLimitMessage);
	if (coerced !== undefined) {
		console.warn(
			`[${componentName}] overLimitMessage was not a string (received ${describeOverLimitMessage(overLimitMessage)}); coerced to "${coerced}" so the overflow rule still reaches Vuetify as a valid result. Pass a string instead.`
		);
	} else {
		// "" for `value`: this is a static config diagnostic about `overLimitMessage` itself, not a
		// report on the field's current content, so there is no real value to check for wide-grapheme
		// content here — passing "" just previews the fieldNoun-aware fallback without the emoji caveat.
		console.warn(
			`[${componentName}] overLimitMessage could not be turned into a usable message (received ${describeOverLimitMessage(overLimitMessage)}); falling back to the default "${defaultOverflowMessage(resolvedMaxLengthValue, fieldNoun, "")}". Pass a non-empty string instead.`
		);
	}
}

function describeOverLimitMessage(overLimitMessage: unknown): string {
	try {
		return String(overLimitMessage);
	} catch {
		return "<unprintable>";
	}
}
